import { useEffect } from 'react';
import { TriangleAlert } from 'lucide-react';
import { useAiCompress } from '@/features/ai/useAI';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import type { RoutineStep } from '@/types';

interface Props {
  steps: RoutineStep[];
  routineName: string;
  onApply: (steps: RoutineStep[]) => void;
  onClose: () => void;
}

export function RoutineCompressionDialog({ steps, routineName, onApply, onClose }: Props) {
  const { data, run, loading, error } = useAiCompress();

  const total = steps.reduce((sum, s) => sum + s.durationMinutes, 0);
  useEffect(() => {
    void run({
      routineSteps: steps.map((s) => ({ name: s.name, durationMinutes: s.durationMinutes })),
      targetTotalMinutes: Math.max(5, Math.round(total * 0.7)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => {
    if (!data) return;
    const kept: RoutineStep[] = data.steps
      .filter((s) => s.kept)
      .sort((a, b) => a.originalIndex - b.originalIndex)
      .map((s) => {
        const original = steps[s.originalIndex];
        if (!original) return null;
        // Preserve all original metadata; only override durationMinutes.
        return { ...original, durationMinutes: s.durationMinutes };
      })
      .filter((s): s is RoutineStep => s !== null);
    onApply(kept);
    onClose();
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogTitle>Compress {routineName}</DialogTitle>
        {loading ? (
          <div className="mt-3 space-y-2">
            {steps.map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : error ? (
          <p className="mt-3 flex items-center gap-1.5 text-body text-t1">
            <TriangleAlert className="size-4 shrink-0 text-warn" aria-hidden />
            Something went wrong — try again?
          </p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {data?.steps.map((s, i) => (
              <li
                key={`${s.name}-${i}`}
                className={
                  s.kept
                    ? 'rounded-md border border-line-2 bg-ok-bg px-2 py-1 text-body text-t1'
                    : 'rounded-md border border-line-2 bg-warn-bg px-2 py-1 text-body text-t2 line-through'
                }
              >
                {s.name} — <span className="font-mono tabular-nums">{s.durationMinutes}m</span>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" size="md" onClick={onClose}>
            Discard
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={apply}
            disabled={!data || loading}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
